import { useTranslation } from 'react-i18next'
import { ArrowLeftRight } from 'lucide-react'
import type { MatchDetail } from '@brochure/shared'
import { previewRearrangement, type RearrangeState } from '@/lib/rearrange'
import { cn } from '@/lib/utils'
import { AirballButton } from './AirballButton'
import { CupTriangle, type TeamTone } from './CupTriangle'
import { ScrollingName } from '@/components/ui/ScrollingName'

type Team = MatchDetail['homeTeam']

/** Una metà del tavolo: squadra, bicchieri presi, stato oro/argento e se può chiedere lo spostamento. */
export type TableSide = {
  team: Team
  taken: Set<number>
  /** Fase del pareggio: i bicchieri rimasti di questa squadra diventano oro. */
  gold: boolean
  silverFlash: boolean
  /** Il bottone "Sposta" su questo triangolo (lo chiede la squadra avversaria). */
  canRearrange?: boolean
}

type MatchBoardProps = {
  layout: number[][]
  maxCups: number
  home: TableSide
  away: TableSide
  /** Spostamento in corso: su quale triangolo e con quali mosse (anteprima prima della conferma). */
  rearrange?: { targetTeamId: string; state: RearrangeState } | null
  /** Bicchieri toccabili (arbitro, partita live, nessuna richiesta in corso). */
  interactive?: boolean
  /** Assente = pagina pubblica, niente bicchierino dell'airball. */
  airball?: { enabled: boolean; onOpen: () => void }
  onCupClick?: (side: 'home' | 'away', cupIndex: number) => void
  onStartRearrange?: (targetTeamId: string) => void
}

const DOT: Record<TeamTone, string> = { sea: 'bg-sea', coral: 'bg-coral' }

/**
 * Il tavolo visto dall'alto, sul piano della carta nautica: triangolo di casa in alto, rete tratteggiata al centro
 * (col conto dei bicchieri e il bicchierino dell'airball), triangolo in trasferta in basso con la punta verso la rete.
 * Solo presentazione: stato e chiamate API restano nel genitore (MatchDashboard, MatchPage).
 */
export function MatchBoard({ layout, maxCups, home, away, rearrange, interactive = false, airball, onCupClick, onStartRearrange }: MatchBoardProps) {
  const { t } = useTranslation()

  function rack(side: 'home' | 'away', data: TableSide, tone: TeamTone) {
    const isTarget = rearrange?.targetTeamId === data.team.id
    const preview = isTarget ? previewRearrangement(data.taken, rearrange!.state) : null
    return (
      <CupTriangle
        layout={layout}
        taken={preview?.taken ?? data.taken}
        tone={tone}
        reversed={side === 'away'}
        gold={data.gold}
        silverFlash={data.silverFlash}
        targets={preview?.targets}
        moved={preview?.moved}
        selected={isTarget ? rearrange!.state.pendingFrom : null}
        disabled={!interactive || (!!rearrange && !isTarget)}
        onCupClick={onCupClick && ((i) => onCupClick(side, i))}
      />
    )
  }

  function label(data: TableSide, tone: TeamTone) {
    const left = maxCups - data.taken.size
    return (
      <div className="flex min-h-11 items-center gap-2.5">
        <span aria-hidden="true" className={cn('size-2.5 shrink-0 rounded-full', DOT[tone])} />
        <ScrollingName className="flex-1 font-display text-lg font-medium text-paper">{data.team.name}</ScrollingName>
        <span aria-label={t('stoccoPong.match.cupsLeft', { left, total: maxCups })} className="font-body text-sm font-semibold tabular-nums text-paper/60">
          {left}/{maxCups}
        </span>
        {data.canRearrange && onStartRearrange && !rearrange && (
          <button
            type="button"
            onClick={() => onStartRearrange(data.team.id)}
            aria-label={t('admin.stoccopong.match.rearrange.button')}
            className="inline-flex min-h-11 items-center gap-1.5 rounded-full border border-paper/25 px-3.5 font-body text-sm font-semibold text-paper transition-colors hover:bg-paper/10"
          >
            <ArrowLeftRight className="size-4" aria-hidden="true" />
            {t('admin.stoccopong.match.rearrange.short')}
          </button>
        )}
      </div>
    )
  }

  const onTable = home.taken.size + away.taken.size

  return (
    <div className="table-felt rounded-[1.75rem] border border-paper/10 px-4 py-3 sm:px-6">
      {label(home, 'sea')}
      <div className="mt-2">{rack('home', home, 'sea')}</div>

      {/* La rete: il conto dei bicchieri al centro e, per l'arbitro, il bicchierino dell'airball in fondo a destra. */}
      <div className="relative my-6 flex min-h-12 items-center justify-center">
        <span aria-hidden="true" className="absolute inset-x-0 top-1/2 border-t-2 border-dashed border-paper/25" />
        <span className="relative rounded-full border border-paper/15 bg-ink px-3.5 py-1.5 font-body text-xs font-semibold tabular-nums text-paper/75">
          {t('stoccoPong.match.table')} {onTable}/{maxCups * 2}
        </span>
        {airball && (
          <span className="absolute right-0 top-1/2 -translate-y-1/2">
            <AirballButton disabled={!interactive || !airball.enabled || !!rearrange} onClick={airball.onOpen} />
          </span>
        )}
      </div>

      <div className="mb-2">{rack('away', away, 'coral')}</div>
      {label(away, 'coral')}
    </div>
  )
}
